import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  CButton,
  CBadge,
  CCard,
  CCardBody,
  CCardFooter,
  CCardHeader,
  CCol,
  CForm,
  CFormInput,
  CFormSelect,
  CFormText,
  CRow,
  CSpinner,
} from "@coreui/react";

import { apiClient } from "services/Api";
import TextEditor from "components/forms/TextEditor";
import ToastNotification from "components/globals/ToastNotification";
import { triggerAlert } from "components/globals/AlertNotification";

const typologyOptions = [
  "SI Inovação",
  "SI Internacionalização",
  "SI Qualificação",
  "SI I&DT",
  "SI I&DT Co-Promoção",
  "Projeto IDI",
  "Outros",
];

const LearnedLesson = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState(null);
  const [toast, setToast] = useState(null);
  const [loading, setLoading] = useState(false);
  const isViewOnly = new URLSearchParams(location.search).get("view") === "1";

  const fetchLessonDetails = async () => {
    try {
      const response = await apiClient.get(`/learned-lessons/${id}/`);
      setFormData(response.data);
    } catch (error) {
      triggerAlert("Lição apreendida não encontrada!", "error");
      navigate("/licoes-aprendidas");
    }
  };

  useEffect(() => {
    if (id) {
      fetchLessonDetails();
    } else {
      setFormData({
        typology: "",
        entity: "",
        description: "",
        summary_sentence: "",
        form_field: "",
        lessons: "",
      });
    }
  }, [id]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: value === "" ? null : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (id) {
        await apiClient.put(`/learned-lessons/${id}/`, formData);
        triggerAlert("Lição apreendida atualizada com sucesso!", "success");
      } else {
        await apiClient.post("/learned-lessons/", formData);
        triggerAlert("Lição apreendida criada com sucesso!", "success");
      }
      navigate("/licoes-aprendidas");
    } catch (error) {
      const errorMessage =
        error.response?.data?.error || "Erro ao gravar lição apreendida";
      setToast({
        title: "Lições Apreendidas",
        message: errorMessage,
        type: "error",
      });
      setLoading(false);
    }
  };

  if (!formData) {
    return (
      <div className="text-center">
        <CSpinner color="primary" />
      </div>
    );
  }

  if (isViewOnly) {
    return (
      <div className="container mt-4">
        <CCard className="shadow-sm">
          <CCardHeader className="d-flex justify-content-between align-items-center">
            <div>
              <h4 className="mb-1">Detalhes da Lição Apreendida</h4>
              <div className="text-medium-emphasis small">
                #{formData.id || "-"} • {formData.created_at || "-"}
              </div>
            </div>
            <div className="d-flex gap-2">
              <CButton
                color="info"
                className="text-white"
                onClick={() => navigate(`/licoes-aprendidas/${id}`)}
              >
                Editar
              </CButton>
              <CButton
                color="secondary"
                variant="outline"
                onClick={() => navigate("/licoes-aprendidas")}
              >
                Voltar
              </CButton>
            </div>
          </CCardHeader>
          <CCardBody className="d-flex flex-column gap-4">
            <div className="d-flex flex-wrap align-items-center gap-2">
              {formData.typology && (
                <CBadge color="info">{formData.typology}</CBadge>
              )}
              <div className="text-medium-emphasis small">
                {formData.created_by_name || "-"}
              </div>
            </div>

            <CRow className="g-3">
              <CCol md={6}>
                <div className="border rounded-3 p-3 bg-body-tertiary h-100">
                  <div className="text-uppercase text-medium-emphasis small">
                    Entidade
                  </div>
                  <div className="fw-semibold">
                    {formData.entity || "-"}
                  </div>
                </div>
              </CCol>
              <CCol md={6}>
                <div className="border rounded-3 p-3 bg-body-tertiary h-100">
                  <div className="text-uppercase text-medium-emphasis small">
                    Frase Resumo
                  </div>
                  <div className="fw-semibold">
                    {formData.summary_sentence || "-"}
                  </div>
                </div>
              </CCol>
              <CCol md={12}>
                <div className="border rounded-3 p-3 bg-body-tertiary">
                  <div className="text-uppercase text-medium-emphasis small">
                    Campo Formulário
                  </div>
                  <div className="fw-semibold">
                    {formData.form_field || "-"}
                  </div>
                </div>
              </CCol>
            </CRow>

            <div className="border rounded-3 p-3 bg-body-tertiary">
              <div className="text-uppercase text-medium-emphasis small">
                Descrição
              </div>
              <div
                className="mt-2"
                dangerouslySetInnerHTML={{
                  __html: formData.description || "-",
                }}
              />
            </div>

            <div className="border rounded-3 p-3 bg-body-tertiary">
              <div className="text-uppercase text-medium-emphasis small">
                Lições
              </div>
              <div
                className="mt-2"
                dangerouslySetInnerHTML={{
                  __html: formData.lessons || "-",
                }}
              />
            </div>
          </CCardBody>
        </CCard>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <ToastNotification toast={toast} setToast={setToast} />

      <CCard className="shadow-sm">
        <CCardHeader className="d-flex justify-content-between align-items-center">
          <div>
            <h4 className="mb-1">
              {id ? "Editar Lição Apreendida" : "Nova Lição Apreendida"}
            </h4>
            <div className="text-medium-emphasis small">
              Registe uma lição de forma clara e objetiva.
            </div>
          </div>
          <div className="d-flex gap-2">
            <CButton
              color="secondary"
              variant="outline"
              onClick={() => navigate("/licoes-aprendidas")}
              disabled={loading}
            >
              Cancelar
            </CButton>
            <CButton color="info" className="text-white" onClick={handleSubmit}>
              {loading ? "A guardar..." : "Guardar"}
            </CButton>
          </div>
        </CCardHeader>
        <CCardBody>
          <CForm onSubmit={handleSubmit}>
            <CFormText className="text-muted mb-4">
              Campos obrigatórios assinalados com *
            </CFormText>

            <div className="border rounded-3 p-3 bg-body-tertiary mb-4">
              <div className="text-uppercase text-medium-emphasis small mb-3">
                Identificação
              </div>
              <CRow className="g-3">
                <CCol md={6}>
                  <CFormSelect
                    label="Tipologia *"
                    name="typology"
                    value={formData.typology || ""}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Selecione...</option>
                    {typologyOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </CFormSelect>
                </CCol>
                <CCol md={6}>
                  <CFormInput
                    label="Entidade *"
                    name="entity"
                    value={formData.entity || ""}
                    onChange={handleInputChange}
                    required
                  />
                </CCol>
                <CCol md={6}>
                  <CFormInput
                    label="Frase Resumo"
                    name="summary_sentence"
                    value={formData.summary_sentence || ""}
                    onChange={handleInputChange}
                  />
                </CCol>
                <CCol md={6}>
                  <CFormInput
                    label="Campo Formulário"
                    name="form_field"
                    value={formData.form_field || ""}
                    onChange={handleInputChange}
                  />
                </CCol>
              </CRow>
            </div>

            <div className="border rounded-3 p-3 bg-body-tertiary mb-4">
              <div className="text-uppercase text-medium-emphasis small mb-3">
                Conteúdo
              </div>
              <TextEditor
                label="Descrição"
                value={formData.description || ""}
                onChange={(value) =>
                  setFormData((prev) => ({ ...prev, description: value }))
                }
                className="mb-3"
              />
              <TextEditor
                label="Lições"
                value={formData.lessons || ""}
                onChange={(value) =>
                  setFormData((prev) => ({ ...prev, lessons: value }))
                }
                className="mb-3"
              />
            </div>
          </CForm>
        </CCardBody>
      </CCard>
    </div>
  );
};

export default LearnedLesson;
